import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

// Utils

import { getMallList } from 'app/api/mall';
import {
  addOffer,
  getOfferById,
  getTemplate,
  updateOffer,
} from 'app/api/offer';
import { getStoreListOnMallID } from 'app/api/store';
import {
  offerValidationRules,
  validate,
} from 'app/constants/validation-constants';
import Message from 'app/utils/Message';
import { dataformatChangesYearToDate } from 'app/utils/Parsers';
import Toast from 'app/utils/Toast';
// import { validate } from './validationCheck';

// An offer whose end date has passed is treated as inactive: in the edit form
// its Status toggle is forced OFF and disabled (an expired offer cannot be
// active). Comparison is date-only, and the end date itself is inclusive
// (an offer ending today is still active).
export const isOfferExpired = (endDate) => {
  if (!endDate) return false;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return end < today;
};

const useCreateOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templateList, setTemplateList] = useState([]);
  const [mallList, setMallList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [isDisabled, setisDisabled] = useState(false);

  const [pageLoading, setPageLoading] = useState(false);
  const storeId = location?.state?.id ? location?.state?.id : '';
  const [templatehideonEdit, setTemplatehideonEdit] = useState(
    storeId ? false : true
  );
  const [error, setErrors] = useState({});
  const [values, setValues] = useState({
    title: '',
    description: '',
    mallId: null,
    storeId: '',
    templateId: null,
    status: true,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    getMallListforDropdown();
    getTemplateDropdown();
  }, []);

  useEffect(() => {
    loadStoreData(storeId);
  }, []);

  async function getMallListforDropdown() {
    setPageLoading(true);
    let response = await getMallList(1);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.malls?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.name,
        };
      });
      setMallList(temp);
    } else {
      Toast.error(
        response?.data ? response?.data : Message?.Error?.COMMON_MESSAGE
      );
    }
  }
  async function getStoreListDropdown(value) {
    setPageLoading(true);
    let response = await getStoreListOnMallID(value);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.storeName,
        };
      });
      setStoreList(temp);
    } else {
      Toast.error(
        response?.data ? response?.data : Message?.Error?.COMMON_MESSAGE
      );
    }
  }
  const getTemplateDropdown = async () => {
    setPageLoading(true);
    let response = await getTemplate(1);
    //let response = await { ...templateListresponse };
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.title,
        };
      });
      setTemplateList(temp);
    } else {
      Toast.error(
        response?.data ? response?.data : Message?.Error?.COMMON_MESSAGE
      );
    }
  };

  const loadStoreData = async (id) => {
    if (!id) {
      return;
    }
    setPageLoading(true);
    let response = await getOfferById(id);
    setPageLoading(false);
    if (response?.responseCode === 200) {
      let responsedata = response?.data;
      setValues((prevState) => ({
        ...responsedata,
        mallId: responsedata?.mallId.id,
        storeId: responsedata?.storeId.id,
        templateId: responsedata?.templateId.id,
        endDate: dataformatChangesYearToDate(responsedata?.endDate),
        startDate: dataformatChangesYearToDate(responsedata?.startDate),
      }));
      getStoreListDropdown(responsedata?.mallId?.id);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  };

  const callAddOrUpdateAPI = async () => {
    setPageLoading(true);
    let response = {};

    let senddata = {
      ...values,
      // An expired offer is inactive regardless of the stored toggle — persist
      // it as false so the dashboard stays consistent after any save.
      status: isOfferExpired(values.endDate) ? false : values.status,
      // parentId: values.categoryId,
      // categoryId: values?.subCategoryId,
    };

    // delete senddata?.subCategoryId;
    // delete senddata?.parentId;
    // delete senddata.endDate;

    if (storeId) {
      response = await updateOffer(storeId, { ...senddata });
    } else {
      response = await addOffer({ ...senddata });
    }
    setPageLoading(false);
    if (response?.responseCode === 200) {
      Toast.success(
        storeId
          ? Message.Offers.UPDATED_SUCCESS
          : Message.Offers.CREATED_SUCCESS
      );
      navigate(-1);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  };

  const handleCreateOffer = (e) => {
    e.preventDefault();
    const validationErrors = validate(values, offerValidationRules);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      callAddOrUpdateAPI();
    }
  };

  function handleMallChange(e) {
    if (e && e.target) {
      const { name, value } = e.target;
      setStoreList([]);

      getStoreListDropdown(value);

      setValues({
        ...values,
        [name]: value,
        storeId: '',
      });

      if (error[name]) {
        setErrors({
          ...error,
          [name]: '',
          storeId: '',
        });
      }
    }
  }

  const handleChange = (e) => {
    if (e && e.target) {
      const { name, value } = e.target;
      if (name === 'templateId' && value) {
        setTemplatehideonEdit(true);
        setisDisabled(false);
      }

      setValues({
        ...values,
        [name]: value,
      });

      if (error[name]) {
        setErrors({
          ...error,
          [name]: '',
        });
      }
    }
  };

  const setChange = (name, val) => {
    setValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
    setErrors({
      ...error,
      [name]: '',
    });
  };

  return {
    templateList,
    storeList,
    storeId,
    mallList,
    handleChange,
    values,
    error,
    handleCreateOffer,
    pageLoading,
    setChange,
    navigate,
    isDisabled,
    setisDisabled,
    handleMallChange,
    templatehideonEdit,
    isExpired: isOfferExpired(values.endDate),
  };
};

export default useCreateOffer;
